import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Add from '@mui/icons-material/Add';
import Search from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';
import { toast } from 'react-toastify';

const ServiceConfig = () => {
  const { theme } = useContext(ThemeContext);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [serviceMacroFilter, setServiceMacroFilter] = useState('');
  const [macros, setMacros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [parentIdForNew, setParentIdForNew] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('todos'); // todos | ativos | desabilitados
  const [form, setForm] = useState({
    parent_id: null,
    name: '',
    description: '',
    is_enabled: true,
    internal_available: true,
    public_available: true,
    macro_id: null,
    accept_all_categories: true,
    category_ids: [],
    order: 0,
    default_priority: '',
    default_sla_minutes: '',
    block_close: false,
  });

  const loadServices = async () => {
    try {
      const { data } = await http.get('/services');
      setServices(data || []);
    } catch (e) {
      toast.error('Erro ao carregar serviços.');
    }
  };

  const loadCategories = async () => {
    try {
      const { data } = await http.get('/ticket-categories');
      setCategories(data || []);
    } catch (e) {
      toast.error('Erro ao carregar categorias.');
    }
  };

  const loadMacros = async () => {
    try {
      const { data } = await http.get('/macros?for_select=1');
      setMacros(data || []);
    } catch (e) {
      toast.error('Erro ao carregar macros.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadCategories();
      await loadServices();
      await loadMacros();
      setLoading(false);
    })();
  }, []);

  const openCreate = (parentService = null) => {
    setEditingId(null);
    setParentIdForNew(parentService?.id ?? null);
    setForm({
      parent_id: parentService?.id ?? null,
      name: '',
      description: '',
      is_enabled: true,
      internal_available: true,
      public_available: true,
      macro_id: null,
      accept_all_categories: true,
      category_ids: [],
      order: parentService ? filhosDe(parentService).length : services.length,
      default_priority: '',
      default_sla_minutes: '',
      block_close: false,
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setParentIdForNew(null);
    const categoryIds = (item.categories || []).map((c) => c.id);
    setForm({
      parent_id: item.parent_id ?? null,
      name: item.name,
      description: item.description || '',
      is_enabled: item.is_enabled !== false,
      internal_available: item.internal_available !== false,
      public_available: item.public_available !== false,
      macro_id: item.macro_id ?? null,
      accept_all_categories: item.accept_all_categories !== false,
      category_ids: categoryIds,
      order: item.order ?? 0,
      default_priority: item.default_priority ?? '',
      default_sla_minutes: item.default_sla_minutes ?? '',
      block_close: item.block_close === true,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.warn('Informe o nome.');
      return;
    }
    if (!form.accept_all_categories && (!form.category_ids || form.category_ids.length === 0)) {
      toast.warn('Selecione ao menos uma categoria ou marque "Aceitar todas as categorias".');
      return;
    }
    const payload = {
      parent_id: form.parent_id || null,
      name: form.name.trim(),
      description: form.description?.trim() || null,
      is_enabled: form.is_enabled,
      internal_available: form.internal_available,
      public_available: form.public_available,
      macro_id: form.macro_id || null,
      accept_all_categories: form.accept_all_categories,
      order: form.order,
      default_priority: form.default_priority === '' || form.default_priority == null ? null : Number(form.default_priority),
      default_sla_minutes: form.default_sla_minutes === '' || form.default_sla_minutes == null ? null : Number(form.default_sla_minutes),
      block_close: form.block_close,
    };
    if (!form.accept_all_categories && form.category_ids?.length) {
      payload.category_ids = form.category_ids;
    }
    try {
      if (editingId) {
        await http.put(`/services/${editingId}`, payload);
        toast.success('Salvo com sucesso.');
      } else {
        await http.post('/services', payload);
        toast.success('Serviço criado.');
      }
      setModalOpen(false);
      await loadServices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao salvar.');
    }
  };

  const handleDelete = async (id, hasSubservices) => {
    if (hasSubservices) {
      toast.warn('Exclua primeiro os subserviços.');
      return;
    }
    if (!window.confirm('Excluir este serviço?')) return;
    try {
      await http.delete(`/services/${id}`);
      toast.success('Excluído.');
      await loadServices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao excluir.');
    }
  };

  // Duplica o serviço (item 23): cria "Cópia de ..." com subserviços e categorias.
  const handleClone = async (id) => {
    try {
      await http.post(`/services/${id}/clone`);
      toast.success('Serviço duplicado.');
      await loadServices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível duplicar o serviço.');
    }
  };

  // Os filhos vêm em `subservices_tree` -- em snake_case, porque o Eloquent
  // converte o nome da relação (`subservicesTree`) ao serializar. Aceitar as
  // três formas evita que a tela dependa de qual endpoint respondeu:
  // `subservices` é o que a API mandava antes, com um nível só.
  const filhosDe = (item) =>
    item.subservices_tree || item.subservicesTree || item.subservices || [];

  const combina = (item) => {
    const termo = busca.trim().toLowerCase();

    const passaNoTexto =
      !termo ||
      (item.name || '').toLowerCase().includes(termo) ||
      (item.description || '').toLowerCase().includes(termo);

    const passaNaSituacao =
      situacao === 'todos' ||
      (situacao === 'ativos' && item.is_enabled !== false) ||
      (situacao === 'desabilitados' && item.is_enabled === false);

    return passaNoTexto && passaNaSituacao;
  };

  /**
   * Filtra a árvore mantendo o CAMINHO até quem combina.
   *
   * Um serviço que não combina fica se algum descendente combinar -- sem
   * isso, procurar por um subserviço de terceiro nível não acharia nada,
   * porque o pai dele não tem o termo no nome.
   */
  const filtrarArvore = (itens) =>
    itens
      .map((item) => {
        const filhos = filtrarArvore(filhosDe(item));
        if (!combina(item) && filhos.length === 0) return null;
        return { ...item, subservices_tree: filhos, subservices: filhos };
      })
      .filter(Boolean);

  const arvore = filtrarArvore(services);

  const contar = (itens) =>
    itens.reduce((n, i) => n + 1 + contar(filhosDe(i)), 0);

  const totalNaBase = contar(services);
  const totalVisivel = contar(arvore);
  const filtrando = busca.trim() !== '' || situacao !== 'todos';

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isSubservice = (item) => item.parent_id != null;
  const modalTitle = editingId
    ? (form.parent_id ? 'Editar subserviço' : 'Editar serviço')
    : (form.parent_id ? 'Novo subserviço' : 'Novo serviço');

  /**
   * Desenha um serviço e, recursivamente, os filhos dele.
   *
   * Antes a tela tinha dois blocos quase idênticos, um para serviço e outro
   * para subserviço -- e por isso só desenhava dois níveis. A importação do
   * Movidesk trouxe uma árvore de QUATRO: 107 serviços no terceiro nível e
   * 16 no quarto ficavam invisíveis, impossíveis de editar ou desabilitar.
   */
  const renderizarNo = (item, nivel) => {
    const filhos = filhosDe(item);
    const temFilhos = filhos.length > 0;
    // Buscando, tudo já vem aberto: esconder o resultado atrás de um clique
    // derrota o propósito da busca.
    const aberto = filtrando || expandedIds.has(item.id);

    return (
      <Card key={item.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {temFilhos ? (
                <IconButton size="small" onClick={() => toggleExpand(item.id)}>
                  {aberto ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              ) : (
                <Box sx={{ width: 34 }} />
              )}
              <Box>
                <Typography variant={nivel === 0 ? 'subtitle2' : 'body2'} fontWeight={600}>
                  {item.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.description || 'Sem descrição'}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {item.is_enabled === false && (
                    <Chip size="small" label="Desabilitado" color="default" sx={{ mr: 0.5 }} />
                  )}
                  {temFilhos && (
                    <Chip size="small" label={`${filhos.length} subserviço(s)`} sx={{ mr: 0.5 }} />
                  )}
                  {nivel === 0 && (item.accept_all_categories ? (
                    <Chip size="small" label="Todas as categorias" sx={{ mr: 0.5 }} />
                  ) : (
                    <Chip size="small" label={`${(item.categories || []).length} categorias`} sx={{ mr: 0.5 }} />
                  ))}
                  {item.internal_available && <Chip size="small" label="Interno" sx={{ mr: 0.5 }} />}
                  {item.public_available && <Chip size="small" label="Público" />}
                </Box>
              </Box>
            </Box>
            <Box>
              <Button size="small" onClick={() => openCreate(item)} sx={{ textTransform: 'none', mr: 0.5 }}>
                Subserviço
              </Button>
              <Button size="small" onClick={() => openEdit(item)} sx={{ textTransform: 'none', mr: 0.5 }}>
                Editar
              </Button>
              <Button size="small" onClick={() => handleClone(item.id)} sx={{ textTransform: 'none', mr: 0.5 }}>
                Duplicar
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => handleDelete(item.id, temFilhos)}
                sx={{ textTransform: 'none' }}
                disabled={temFilhos}
              >
                Excluir
              </Button>
            </Box>
          </Box>
        </CardContent>
        {temFilhos && (
          <Collapse in={aberto}>
            <Box sx={{ pl: 3, pr: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filhos.map((filho) => renderizarNo(filho, nivel + 1))}
            </Box>
          </Collapse>
        )}
      </Card>
    );
  };

  if (loading) {
    return <Typography color="text.secondary">Carregando...</Typography>;
  }

  return (
    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
          Serviços e Subserviços
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          Crie serviços (ex: Comercial) e subserviços (ex: Cancelamento). Defina disponibilidade para tickets internos e públicos, macro (em breve) e categorias permitidas.
        </Typography>
        <Button variant="contained" onClick={() => openCreate()} sx={{ textTransform: 'none', mb: 2 }}>
          Novo serviço
        </Button>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar serviço ou subserviço"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {[
              ['todos', 'Todos'],
              ['ativos', 'Ativos'],
              ['desabilitados', 'Desabilitados'],
            ].map(([valor, rotulo]) => (
              <Chip
                key={valor}
                label={rotulo}
                size="small"
                clickable
                onClick={() => setSituacao(valor)}
                color={situacao === valor ? 'primary' : 'default'}
                variant={situacao === valor ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {filtrando
            ? `${totalVisivel} de ${totalNaBase} serviço(s)`
            : `${totalNaBase} serviço(s) no total`}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {arvore.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Nenhum serviço encontrado com esse filtro.
            </Typography>
          )}
          {arvore.map((s) => renderizarNo(s, 0))}
        </Box>
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{modalTitle}</DialogTitle>
          <DialogContent>
            {editingId && form.parent_id != null && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Subserviço do serviço: {services.find((x) => x.id === form.parent_id)?.name ?? '-'}
              </Typography>
            )}
            {!editingId && parentIdForNew != null && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Será subserviço de: {services.find((x) => x.id === parentIdForNew)?.name ?? '-'}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Nome"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrição"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch checked={form.is_enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })} />}
              label="Habilitado para uso"
            />
            <br />
            <FormControlLabel
              control={<Switch checked={form.internal_available} onChange={(e) => setForm({ ...form, internal_available: e.target.checked })} />}
              label="Disponível para tickets internos"
            />
            <br />
            <FormControlLabel
              control={<Switch checked={form.public_available} onChange={(e) => setForm({ ...form, public_available: e.target.checked })} />}
              label="Disponível para tickets públicos"
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Macro aplicada a este serviço
              </Typography>
              <Select
                fullWidth
                size="small"
                value={form.macro_id ?? ''}
                displayEmpty
                onChange={(e) => setForm({ ...form, macro_id: e.target.value || null })}
                onClose={() => setServiceMacroFilter('')}
                MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 380 } } }}
              >
                {/* Busca: filtra as macros pelo nome. stopPropagation evita o Select
                    fechar/selecionar ao interagir com o campo. */}
                <Box sx={{ px: 1, py: 0.5 }} onKeyDown={(e) => e.stopPropagation()} onClickCapture={(e) => e.stopPropagation()}>
                  <TextField
                    size="small"
                    autoFocus
                    fullWidth
                    placeholder="Buscar macro..."
                    value={serviceMacroFilter}
                    onChange={(e) => setServiceMacroFilter(e.target.value)}
                  />
                </Box>
                <MenuItem value="">Nenhum</MenuItem>
                {macros
                  .filter((m) => m.is_enabled !== false)
                  .filter((m) => (m.name || '').toLowerCase().includes(serviceMacroFilter.trim().toLowerCase()))
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                  ))}
              </Select>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Urgência padrão
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={form.default_priority === '' || form.default_priority == null ? '' : form.default_priority}
                  displayEmpty
                  onChange={(e) => setForm({ ...form, default_priority: e.target.value })}
                >
                  <MenuItem value="">Não definir (usa a do ticket)</MenuItem>
                  <MenuItem value={0}>Baixa</MenuItem>
                  <MenuItem value={1}>Média</MenuItem>
                  <MenuItem value={2}>Alta</MenuItem>
                  <MenuItem value={3}>Urgente</MenuItem>
                </Select>
              </Box>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  SLA padrão (minutos)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.default_sla_minutes}
                  onChange={(e) => setForm({ ...form, default_sla_minutes: e.target.value })}
                  inputProps={{ min: 0 }}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch checked={form.block_close} onChange={(e) => setForm({ ...form, block_close: e.target.checked })} />}
                label="Bloquear fechamento enquanto este serviço estiver no ticket"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.accept_all_categories}
                    onChange={(e) => setForm({ ...form, accept_all_categories: e.target.checked })}
                  />
                }
                label="Aceitar todas as categorias (ticket aceita qualquer categoria cadastrada)"
              />
              {!form.accept_all_categories && (
                <Typography variant="caption" display="block" sx={{ mt: 1, mb: 0.5 }}>
                  Selecione as categorias permitidas para este serviço:
                </Typography>
              )}
              {!form.accept_all_categories && (
                <Select
                  fullWidth
                  multiple
                  size="small"
                  value={form.category_ids || []}
                  onChange={(e) => setForm({ ...form, category_ids: e.target.value })}
                  renderValue={(selected) => selected.map((id) => categories.find((c) => c.id === id)?.name).filter(Boolean).join(', ')}
                  sx={{ mt: 0.5 }}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={(form.category_ids || []).indexOf(c.id) > -1} />
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ServiceConfig;
